/**
 * OKLCH color interpolation for animation keyframes. GSAP tweens color
 * strings in sRGB, whose midpoints go muddy (audit 0.2); sampling the OKLCH
 * gradient into a handful of stops and letting GSAP lerp between close
 * neighbours is perceptually equivalent to true OKLCH tweening with zero
 * engine changes. No dependencies — ~60 lines of standard color math.
 */

type Oklch = { l: number; c: number; h: number; alpha: number };

const srgbToLinear = (c: number) =>
  c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

const linearToSrgb = (c: number) =>
  c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;

const parseHex = (color: string): [number, number, number, number] | null => {
  // scenes can carry malformed/missing colors — fall back, never throw
  if (typeof color !== "string") {
    return null;
  }
  const m = color.trim().match(/^#([0-9a-f]{6})([0-9a-f]{2})?$/i);
  if (!m) {
    return null;
  }
  const v = parseInt(m[1], 16);
  const alpha = m[2] ? parseInt(m[2], 16) / 255 : 1;
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255, alpha];
};

const toOklch = (r8: number, g8: number, b8: number, alpha: number): Oklch => {
  const r = srgbToLinear(r8 / 255);
  const g = srgbToLinear(g8 / 255);
  const b = srgbToLinear(b8 / 255);
  // linear sRGB → OKLab (Björn Ottosson's reference matrices)
  const l_ = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m_ = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s_ = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
  const A = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
  const B = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;
  const c = Math.hypot(A, B);
  // chroma ≈ 0 (grays): hue is meaningless noise — zero it so gray ramps
  // don't pick up a stray hue
  const h = c < 1e-6 ? 0 : (Math.atan2(B, A) * 180) / Math.PI;
  return { l: L, c, h: (h + 360) % 360, alpha };
};

const fromOklch = ({ l: L, c, h, alpha }: Oklch): string => {
  const hr = (h * Math.PI) / 180;
  const A = c * Math.cos(hr);
  const B = c * Math.sin(hr);
  const l_ = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m_ = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s_ = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;
  const r = linearToSrgb(
    4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_,
  );
  const g = linearToSrgb(
    -1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_,
  );
  const b = linearToSrgb(
    -0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_,
  );
  const to8 = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255);
  if (alpha >= 1) {
    return `#${((to8(r) << 16) | (to8(g) << 8) | to8(b))
      .toString(16)
      .padStart(6, "0")}`;
  }
  return `rgba(${to8(r)}, ${to8(g)}, ${to8(b)}, ${Number(alpha.toFixed(3))})`;
};

/** Samples the OKLCH gradient from → to into `n` CSS color stops (inclusive
 * endpoints, exact at both ends). Hue takes the short way around.
 * "transparent" becomes alpha-0 of the counterpart's hue so fades don't pass
 * through black (audit 0.2). Unparseable colors fall back to a two-stop
 * [from, to] pair — GSAP's sRGB lerp, no worse than before. */
export const oklchStops = (from: string, to: string, n = 8): string[] => {
  const count = Math.max(2, Math.round(n));
  const fromRgb =
    from === "transparent" ? parseHex(to) && [0, 0, 0, 0] : parseHex(from);
  const toRgb =
    to === "transparent" ? parseHex(from) && [0, 0, 0, 0] : parseHex(to);
  if (!fromRgb || !toRgb) {
    return [from, to];
  }
  let a = toOklch(fromRgb[0], fromRgb[1], fromRgb[2], fromRgb[3]);
  let b = toOklch(toRgb[0], toRgb[1], toRgb[2], toRgb[3]);
  if (from === "transparent") {
    const counterpart = toOklch(
      ...(parseHex(to) as [number, number, number, number]),
    );
    a = { ...counterpart, alpha: 0 };
  }
  if (to === "transparent") {
    const counterpart = toOklch(
      ...(parseHex(from) as [number, number, number, number]),
    );
    b = { ...counterpart, alpha: 0 };
  }
  // shortest-path hue; a gray endpoint adopts the other side's hue instead
  // of dragging the ramp through hue 0
  const aHue = a.c < 1e-6 ? b.h : a.h;
  const bHue = b.c < 1e-6 ? a.h : b.h;
  let dh = bHue - aHue;
  if (dh > 180) {
    dh -= 360;
  } else if (dh < -180) {
    dh += 360;
  }
  const stops: string[] = [];
  for (let i = 0; i < count; i++) {
    const t = i / (count - 1);
    if (i === 0) {
      stops.push(from === "transparent" ? fromOklch(a) : from);
      continue;
    }
    if (i === count - 1) {
      stops.push(to === "transparent" ? fromOklch(b) : to);
      continue;
    }
    stops.push(
      fromOklch({
        l: a.l + (b.l - a.l) * t,
        c: a.c + (b.c - a.c) * t,
        h: (aHue + dh * t + 360) % 360,
        alpha: a.alpha + (b.alpha - a.alpha) * t,
      }),
    );
  }
  return stops;
};
